import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';

import { dateFormatter } from '@/utils/dateFormatter';

import type { GroupSubscriptionData } from '../groupSubscriptionTypes';

type ScheduledReductionBannerProps = {
  group: GroupSubscriptionData;
  onModify?: () => void;
  onCancel?: () => void;
};

/**
 * Persistent notice shown while a seat reduction is scheduled. Renders only when
 * `scheduled_seat_count` is set and below the current `max_seats` — a scheduled
 * count equal to (or above) max means there's nothing pending to surface.
 *
 * The member count comes from `seats_terminating` (a payload count) rather than
 * `max_seats - scheduled_seat_count`: the latter is the *seat* delta, which
 * over-counts when the reduction also drops unfilled seats. Only filled members
 * transition to `terminating`, so that count is the honest "who loses access".
 */
export function ScheduledReductionBanner({ group, onModify, onCancel }: ScheduledReductionBannerProps) {
  const { scheduled_seat_count, max_seats, seats_terminating, renews_at } = group;

  // Loose null check: during the deploy window an older Swarm omits this field,
  // so it can be `undefined` at runtime despite the type. Matches the `== null`
  // guards used elsewhere — strict `=== null` would render "undefined seats".
  if (scheduled_seat_count == null || scheduled_seat_count >= max_seats) return null;

  // Force UTC — renews_at is a calendar date stored as UTC midnight.
  const renewsOnLabel = renews_at ? dateFormatter({ timeZone: 'UTC' }).format(new Date(renews_at)) : null;
  const memberLabel = seats_terminating === 1 ? 'member' : 'members';

  return (
    <div className="flex flex-col gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex gap-3">
        <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" aria-hidden="true" />
        <div className="flex flex-col gap-1">
          <p className="font-medium">
            Renewing with {scheduled_seat_count} {scheduled_seat_count === 1 ? 'seat' : 'seats'}
            {renewsOnLabel ? ` on ${renewsOnLabel}` : ''}
          </p>
          <p>
            {seats_terminating > 0
              ? `${seats_terminating} ${memberLabel} will lose access at renewal. They keep access until then.`
              : 'Unused seats will be removed at renewal.'}
          </p>
        </div>
      </div>
      <div className="flex flex-shrink-0 items-center gap-2 self-start sm:self-center">
        {onModify && (
          <button
            type="button"
            onClick={onModify}
            className="rounded-md border border-amber-300 bg-white px-2.5 py-1.5 text-xs font-medium text-amber-900 shadow-sm hover:bg-amber-100"
          >
            Modify
          </button>
        )}
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-amber-300 bg-white px-2.5 py-1.5 text-xs font-medium text-amber-900 shadow-sm hover:bg-amber-100"
          >
            Cancel reduction
          </button>
        )}
      </div>
    </div>
  );
}
